import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, ClearIcon, Flex, Input } from '@modusign/design-system';
import { iconNeutralBase, iconNeutralSlightlyDark } from '@modusign/design-system/colors';
import { endOfDay, format, getDefaultOptions, isEqual, startOfDay } from 'date-fns';
import { CSSProperties, MouseEvent, ReactNode } from 'react';
import { DateRange as RangeDate, Day, DayPicker, DayPickerRangeProps } from 'react-day-picker';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import { DefaultOption } from '@app/libs/dateFns';

interface FieldProps {
  placeholder: string;
  style?: CSSProperties;
  isInvalid?: boolean;
  disabled?: boolean;
  dateFormat?: string;
  onClearDate?: () => void;
}

interface RangeDatePickerProps extends Omit<DayPickerRangeProps, 'mode'> {
  value: RangeDate;
  footer?: ReactNode;
  field: FieldProps;
}

export default function RangeDatePicker({
  footer,
  value,
  field: {
    placeholder,
    style,
    isInvalid = false,
    disabled = false,
    dateFormat = 'yyyy-MM-dd',
    onClearDate,
  },
  ...props
}: RangeDatePickerProps) {
  const { from, to } = value;
  const [isOpen, toggle] = useToggle(false);
  const { locale }: DefaultOption = getDefaultOptions();

  const getInputValue = () => {
    if (from) {
      const fromDate = format(from, dateFormat);
      const toDate = to ? format(to, dateFormat) : '';
      return `${fromDate}~${toDate}`;
    }

    return '';
  };

  const isFromDate = (date: Date) => !!from && isEqual(startOfDay(from), date);
  const isToDate = (date: Date) => !!to && isEqual(to, endOfDay(date));

  return (
    <Box position="relative">
      <OutsideClickHandler onOutsideClick={() => toggle(false)}>
        <Box
          height="100%"
          position="relative"
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          width="100%"
          onClick={() => {
            if (!disabled) {
              toggle();
            }
          }}
        >
          <Input
            backgroundColor={
              disabled ? 'formBackgroundNeutralSlightlyDark' : 'formBackgroundNeutralBase'
            }
            color={disabled ? 'textNeutralVeryLight' : 'textNeutralBase'}
            disabled={disabled}
            isInvalid={isInvalid}
            placeholder={placeholder}
            readOnly
            style={style}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'buttonBorderNeutralSlightlyLight',
                backgroundColor: 'buttonBackgroundNeutralBase',
              },
            }}
            value={getInputValue()}
          />
          {onClearDate && (from || to) && (
            <Flex
              alignItems="center"
              height="36px"
              justifyContent="center"
              position="absolute"
              right={7}
              textAlign="center"
              top={0}
              width="36px"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                onClearDate();
              }}
            >
              <ClearIcon />
            </Flex>
          )}
          <Flex
            alignItems="center"
            height="36px"
            justifyContent="center"
            position="absolute"
            right={0}
            textAlign="center"
            top={0}
            width="36px"
          >
            <FontAwesomeIcon
              color={disabled ? iconNeutralBase : iconNeutralSlightlyDark}
              height="14px"
              icon={faCalendarDay}
              width="13px"
            />
          </Flex>
        </Box>
        {isOpen && (
          <Card
            display="flex"
            flexDirection="column"
            left={0}
            p={4}
            position="absolute"
            top="42px"
            zIndex={1001}
          >
            <DayPicker
              components={{
                Day: (props) => {
                  const { date } = props;
                  const isFrom = isFromDate(date);
                  const isTo = isToDate(date);
                  return (
                    <Box
                      color="black"
                      fontSize={2}
                      sx={{
                        backgroundColor:
                          from && to && (isFrom || isTo)
                            ? 'formBackgroundEmphasisLight'
                            : 'transparent',
                        borderTopLeftRadius: isFrom ? '50%' : 0,
                        borderBottomLeftRadius: isFrom ? '50%' : 0,
                        borderTopRightRadius: isTo ? '50%' : 0,
                        borderBottomRightRadius: isTo ? '50%' : 0,
                        '.rdp-button:hover:not([disabled]):not(.rdp-day_selected)': {
                          backgroundColor: 'iconNeutralVeryLight',
                        },
                        '.rdp-day_today:not(.rdp-day_selected)': {
                          border: '1px solid',
                          borderColor: 'buttonBorderNeutralSlightlyLight',
                          backgroundColor: 'white',
                        },
                        '.rdp-day_selected': {
                          backgroundColor: 'formBackgroundEmphasisDark',
                          color: 'white',
                          fontWeight: 500,
                        },
                        '.rdp-day_range_middle': {
                          backgroundColor: 'formBackgroundEmphasisLight',
                          color: 'black',
                        },
                        '.rdp-day_range_end:not(.rdp-day_range_start), .rdp-day_range_start:not(.rdp-day_range_end)':
                          {
                            borderRadius: '50% !important',
                          },
                      }}
                    >
                      <Day {...props} />
                    </Box>
                  );
                },
              }}
              locale={locale}
              mode="range"
              selected={value}
              {...props}
            />
            {footer}
          </Card>
        )}
      </OutsideClickHandler>
    </Box>
  );
}
